"use client";

import { parseSolutionSteps } from "@/lib/math-engine/solution-steps";

interface SolutionStepsProps {
  solution: string;
  answer?: string;
}

/**
 * Step-by-step solution display.
 *
 * Each newline-separated step from the template renders as a numbered row
 * (number badge + readable text). The final step is highlighted as the
 * conclusion. No card-in-card: rows use dividers, radius comes only from
 * the outer question container.
 */
export function SolutionSteps({ solution, answer }: SolutionStepsProps) {
  const steps = parseSolutionSteps(solution);
  if (steps.length === 0) return null;

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
        Step-by-step solution
      </p>
      <ol className="mt-2 divide-y divide-[var(--color-border)] overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-bg)]">
        {steps.map((step, i) => {
          const isLast = i === steps.length - 1;
          return (
            <li
              key={i}
              className={`flex items-start gap-3 px-3 py-2.5 text-sm leading-relaxed ${
                isLast
                  ? "bg-[var(--color-accent-soft)] text-[var(--color-text)]"
                  : "text-[var(--color-text)]"
              }`}
            >
              <span
                aria-hidden="true"
                className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                  isLast
                  ? "bg-[var(--color-accent)] text-[var(--color-accent-fg)]"
                  : "bg-[var(--color-surface-2)] text-[var(--color-text-muted)]"
                }`}
              >
                {i + 1}
              </span>
              <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">
                {step}
                {isLast && answer ? (
                  <span className="mt-1 block text-xs text-[var(--color-text-muted)]">
                    Answer: {answer}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
